/**
 * Shared types across the app.
 */
import { ReactNode, ReactNodeArray } from "react";
import { TransformMap, ValidationRules } from "objectro/lib/types";
import { JSONSchema7 } from "json-schema";
import { ReactSelectOptionObject } from "./components/InputSelectCustom";

const stringLitArray = <L extends string>(arr: L[]) => arr;
export const maisons = stringLitArray([
  "lvmh",
  "celine",
  "rimowa",
  "guerlain",
  "guerlain-fr",
  "fred",
  "tiffany",
  "tiffany-jp"
]);
export type MaisonIdentifier = typeof maisons[number];

export interface POJO {
  [key: string]: any;
}

export interface JSONSchema7Object extends JSONSchema7, POJO {}

export interface SchemaOrgContext {
  "@language"?: string;
  "@vocab"?: string;
}

export interface SchemaOrg {
  "@context"?: string | SchemaOrgContext;
  "@type": string;
}

export interface GeoCoordinates extends SchemaOrg {
  addressCountry: string;
}

export interface WithIdentifier {
  identifier: string;
}

export interface Place extends SchemaOrg, WithIdentifier {
  name: string;
  geo: GeoCoordinates;
}

export interface LocalBusiness extends Place {
  areaServed: string;
}

export interface Person extends SchemaOrg, WithIdentifier {
  worksFor: Partial<LocalBusiness>;
}

export interface PostalAddress extends SchemaOrg {
  streetAddress: string | (string | any)[] | null | undefined;
  addressLocality: string | null | undefined;
  addressRegion: string | (POJO & WithIdentifier);
  postalCode: string | number | null | undefined;
  addressCountry: string | (POJO & WithIdentifier);
}

// Used in the CustomerRequest/CustomerResponse object
export interface Customer extends SchemaOrg, Partial<WithIdentifier> {
  [propKey: string]: any;
  gender?: string | null | undefined;
  honorificPrefix: string | null | undefined;
  givenName: string;
  alternateGivenName1?: string | null | undefined;
  alternateGivenName2?: string | null | undefined;
  familyName: string;
  alternateFamilyName1?: string | null | undefined;
  alternateFamilyName2?: string | null | undefined;
  birthDate?: string | null | undefined;
  nationality?: string | null | undefined | (POJO & WithIdentifier);
  isAddressValid?: boolean | null;
  isEmailValid?: boolean | null;
  address?:
    | {
        streetAddress?: string | string[] | null | undefined;
        addressLocality?: string | null | undefined;
        addressRegion?: string | null | undefined | (POJO & WithIdentifier);
        postalCode?: string | number | null | undefined;
        addressCountry?: string | null | undefined;
      }
    | null
    | undefined;
}

export interface I18nMessageOptions {
  /** The default text to use if the translation is missing. */
  defaultValue?: string;
  /** The values to replace the `{keywords}` inside the translated message. */
  values?: {
    [replaceKey: string]: React.ReactNode | React.ReactNodeArray;
  };
  /** The language to use. */
  lng?: string;
}

export interface I18nMessage {
  /** The translation key, e.g. `namespace:path.to.translation.key`. */
  translationId: string | string[];
  /** Options to give to i18next */
  options?: I18nMessageOptions;
}

export interface CustomEnum<T> {
  [key: string]: T;
}

export type DesignSize = "xxs" | "xs" | "s" | "m" | "l" | "xl" | "xxl";
export type DesignTheme =
  | "primary"
  | "secondary"
  | "tertiary"
  | "dark"
  | "neutral"
  | "light";

export type TextDirection = "ltr" | "rtl";

export interface WithChildren {
  children?: ReactNode | ReactNodeArray;
}

// Custom Input change event handlers only publish the new value and the input's name
export type InputCustomChangeEventHandler = (
  newValue: any,
  inputName?: string
) => void;

export type CustomerRequestType = "create" | "read" | "update";
export type CustomerResponseErrorType = "failure" | "api_error";

export interface CustomerRequest {
  type: CustomerRequestType;
  requestId: string;
  traceToken: string;
  salesPersonId: string;
  storeId: string;
  storeCountry: string;
  attempt: number;
  data: Customer;
  storeName: string;
  countryName: string;
  signatureId?: string;
  isRemote?: boolean | null | undefined;
}

export interface CustomerResponseObject {
  requestId: string;
  traceToken: string;
  salesPersonId: string;
  storeId: string;
  storeCountry: string;
  storeName: string;
  countryName: string;
  attempt: number;
  data?: Customer; // @TODO when we have Customer interface
  translationId?: string;
  errorType?: CustomerResponseErrorType;
  errorCode?: string | number | null;
  errorMessage?: string;
}

export interface CustomerResponseSuccess {
  requestId: string;
  traceToken: string;
  salesPersonId: string;
  storeId: string;
  storeCountry: string;
  storeName: string;
  countryName: string;
  attempt: number;
  data: Customer; // @TODO when we have Customer interface
}

export interface CustomerResponsePending {
  requestId: string;
  traceToken: string;
  salesPersonId: string;
  storeId: string;
  storeCountry: string;
  storeName: string;
  countryName: string;
  attempt: number;
  data: Customer; // @TODO when we have Customer interface
  hashcode: string; // for pending creation requests
  requestDate: Date;
}

export interface CustomerResponseError {
  requestId: string;
  traceToken: string;
  salesPersonId: string;
  storeId: string;
  storeCountry: string;
  storeName: string;
  countryName: string;
  attempt: number;
  errorType?: CustomerResponseErrorType;
  errorCode?: string | number | null;
  errorMessage?: string;
  translationId?: string;
}

export type CustomerResponse =
  | CustomerResponseObject
  | CustomerResponseSuccess
  | CustomerResponsePending
  | CustomerResponseError;

export interface AzureCustomerRequest {
  fileName: string;
  content: string;
}

export interface CustomerSignatureResponse {
  brand?: string;
  market?: string;
  docType?: string;
  assets: [
    {
      name: string;
      url?: string;
    }
  ];
}

/*  const enums aren't supported in CRA+TypeScript ¯\_(ツ)_/¯
export enum NotificationType {
  Success = "success",
  Error = "error",
  Warning = "warning",
  Info = "info"
} */
export const NOTIFICATION_TYPE = Object.freeze({
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  PENDING: "pending"
});

/*  const enums aren't supported in CRA+TypeScript ¯\_(ツ)_/¯
export enum NotificationAction {
  CustomerDataCreated = "customerData.created",
  CustomerDataUpdated = "customerData.updated",
  CustomerDataFailure = "customerData.failure",
  CustomerDataApiError = "customerData.apiError",
  VerifyCustomerData = "customerData.verify"
} */
export const NOTIFICATION_ACTION = Object.freeze({
  CUSTOMER_DATA_CREATED: "customerData.created",
  CUSTOMER_DATA_UPDATED: "customerData.updated",
  CUSTOMER_DATA_PENDING: "customerData.pending",
  // Client data was rejected, cannot be retried
  CUSTOMER_DATA_FAILURE: "customerData.failure",
  // Client data was not saved due to unforeseen issue, can be retried
  CUSTOMER_DATA_API_ERROR: "customerData.apiError",
  CUSTOMER_DATA_VERIFY: "customerData.verify",
  CUSTOMER_NOT_FOUND: "customerData.notFound"
});

/**
 * The validate function should return an empty object if it is valid,
 * Otherwise it returns an object with any errors for each errored field:
 *
 * ```
 * {
 *   honorificPrefix: [
 *     "Please select a title"
 *   ],
 *   givenName: [
 *     "Please enter your first name"
 *   ],
 *   familyName: [
 *     "Please enter your last name"
 *   ]
 * }
 * ```
 */
export type ConfigurableFormValidateFn = (
  formState: any
) => ConfigurableFormErrorsObject;

/**
 * Custom function to translate strings for use in a configurable form.
 */
export type ConfigurableFormTFn = (
  i18nMessage: string | I18nMessage,
  replaceValues?: any
) => React.ReactNode | React.ReactNodeArray;

/**
 * Custom function to map the configurable form's shared state to the
 * rendered form element's props.
 */
export type ConfigurableFormStateToPropsFn = (
  sharedState?: {
    appState?: object;
    pageState?: object;
    formState?: object;
  },
  props?: object
) => object;

/**
 * Custom function to determine if the form element should be visible or not
 * based on the configurable form's shared state.
 */
export type ConfigurableFormShowOnlyIfFn = (sharedState?: {
  appState?: object;
  pageState?: object;
  formState?: object;
}) => boolean;

export interface ConfigurableFormElements {
  [elementName: string]: (props: any) => JSX.Element | null;
}

export interface ConfigurableFormLabelsObject {
  [labelName: string]: string | I18nMessage | undefined;
}

/**
 * Each configurable form can have three buttons:
 *
 *   - `buttonNext`: Continue to the next step
 *   - `buttonBack`: Go back to the previous step
 *   - `buttonSubmit`: Complete the form
 *
 * These labels can also be translation keys and will be translated by the
 * the translation function specified at `ConfigurableFormProps.t`.
 */
export interface ConfigurableFormLabels extends ConfigurableFormLabelsObject {
  /**
   * Label for the button which allows user to go back a step.
   */
  buttonBack?: string | I18nMessage;
  /**
   * Label for the button which allows user to continue to the next step.
   */
  buttonNext?: string | I18nMessage;
  /**
   * Label for the button which allows user to submit the form data.
   */
  buttonSubmit?: string | I18nMessage;
}

export type ConfigurableFormElementErrors = string[];

/**
 * This is the errors object that the configurable form validation function
 * should return.
 */
export interface ConfigurableFormErrorsObject {
  [elementName: string]: ConfigurableFormElementErrors;
}

export interface ConfigurableFormElementErrorMessageObject
  extends ConfigurableFormLabelsObject {
  /**
   * Generic error message.
   */
  _?: string | I18nMessage;
  /**
   * Error message when value doesn't match `type` in the schema.
   */
  type?: string | I18nMessage;
  /**
   * Error message when value doesn't match `format` in the schema.
   */
  format?: string | I18nMessage;
  /**
   * Error message when value is missing.
   */
  required?: string | I18nMessage;
  /**
   * Error message when value doesn't match an expected value.
   */
  const?: string | I18nMessage;
  /**
   * Error message when value doesn't match any of the expected values.
   */
  enum?: string | I18nMessage;
}

export type ConfigurableFormElementLabels =
  | string
  | ConfigurableFormLabelsObject
  | ConfigurableFormElementErrorMessageObject;

export interface ConfigurableFormElementConfig {
  /**
   * This specifies which component to render the element with.
   *
   * Element components are specified at `ConfigurableFormProps.elements`.
   */
  component?: string;
  /**
   * The ID be used for the HTML input element's `id` attribute.
   * For Field elements this means when you click on the label that it
   * will focus the input element.
   */
  id?: string;
  /**
   * The name corresponds to the form's state object (all the form data).
   * This will also map to the input element's `name` attribute.
   */
  name?: string;
  /**
   * Specifically `Field` elements will need a type specified. This will
   * affect what kind of input element (native or custom) that will be used
   * within the Field.
   */
  type?: string;
  /**
   * The label is used for `Field` components to display a label to accompany
   * the input element. `label` values are also automatically translated by
   * the translation function specified at `ConfigurableFormProps.t` so they
   * can be `I18nMessage` objects as well.
   */
  label?: string | I18nMessage;
  /**
   * Specify any static props that will be passed to the form element when it
   * is rendered.
   *
   * If there is a `props.labels` or `props.inputProps.labels` objects within
   * these will be processed with the translation function specified at
   * `ConfigurableFormProps.t`.
   */
  props?: object;
  /**
   * We can also feed specific app/page/form state to the element's
   * props object. Above specifies the _static_ props, below is an
   * `objectro.transform` map which plucks a value from the source
   * to place at a specific destination, i.e. _dynamic_ props, in case
   * a value in the app, page, or form state changes and needs to be
   * reflected in the element.
   *
   * It's good to note that `objectro.transform` will process the props
   * in order that they're specified. Using the below as an example:
   *
   *   1. `pageState.language` will map to `inputProps.language`
   *   2. `formState.nationality` will map to `inputProps.country`
   *   3. `formState.addressCountry` will map to `inputProps.country`
   *      overwriting step 2, only if it has a value.
   *
   * See: https://lvl99.github.io/objectro/api.html#transform
   *
   * Otherwise you can use a customer function which takes the form's
   * `sharedState` prop, the component's `props` object and returns a new
   * `props` object.
   */
  stateToProps?: TransformMap | ConfigurableFormStateToPropsFn;
  /**
   * We can specify an `objectro.validate` query object here to conditionally
   * show/hide the element based on values in the `appState`, `pageState` and
   * the `formState`.
   *
   * See: https://lvl99.github.io/objectro/api.html#validation-rules
   *
   * Otherwise you can use a customer function which takes the form's
   * `sharedState` prop and returns a boolean.
   */
  showOnlyIf?: boolean | ValidationRules | ConfigurableFormShowOnlyIfFn;
  /**
   * A map of custom error messages to be output to the Field element.
   * This will connect with the generated form/step validation function,
   * which is powered by AJV.
   *
   * This mostly matches the AJV custom error messages API, however I
   * couldn't figure out AJV's one to use in the validation schema, so I put
   * the ability to define it here.
   *
   * See: https://github.com/epoberezkin/ajv-errors
   */
  errorMessage?: string | ConfigurableFormElementErrorMessageObject;
  /**
   * For the Group form element, you can provide an array of elements to render.
   */
  fields?: ConfigurableFormElementConfig[];
}

export interface ConfigurableFormStepConfig {
  /**
   * The list of fields to render.
   */
  fields: ConfigurableFormElementConfig[];
  /**
   * The labels to render for the form's buttons.
   */
  labels?: ConfigurableFormLabels;
  /**
   * A custom validation function to validate the form state.
   */
  validate?: ConfigurableFormValidateFn;
  /**
   * A schema to validate the form state.
   *
   * It's important to note that the form state is shared between all
   * steps. This is by design!
   */
  validateSchema?: JSONSchema7Object;

  buttonsOnTop?: boolean;
}

export interface ConfigurableFormConfig
  extends Partial<ConfigurableFormStepConfig> {
  /**
   * A configurable form can have nested configs for each step.
   */
  steps?: ConfigurableFormStepConfig[];
  /**
   * We can specify any extra schemas to reference. The form's (or step's)
   * `validateSchema` can refer to schemas using the JSON pointer convention.
   *
   * The example below would require a schema specified in the `refSchemas`
   * array to have an `$id` of `formElements` and to also have a valid
   * schema object defined:
   *
   * ```
   * // validateSchema
   * {
   *   type: "object",
   *   properties: {
   *     givenName: { "$ref": "formElements#/definition/givenName" }
   *   }
   * }
   *
   * // refSchemas
   * [{
   *   $id: "formElements",
   *   $schema: "",
   *   definitions: {
   *     givenName: {
   *       type: "string"
   *     }
   *   }
   * }]
   * ```
   */
  refSchemas?: JSONSchema7Object[];
}

export interface AppServiceGoogleAnalytics {
  ua: string;
  options?: any;
}

export interface AppServiceGoogleMaps {
  /** The API key for accessing Google Maps service. */
  key?: string;
  /** The client identifier for accessing Google Maps service. */
  client?: string;
  /** The channel identifier for accessing Google Maps service. */
  channel?: string;
  /** Set what specific libraries are being used for Google Maps. */
  libraries?: string;
  /** initial language */
  language?: string;
  /** Fire a callback after initialising Google Maps. */
  callback?: Function;
  /** Exclude Google Maps for specific store countries. */
  excludeCountries?: string[];
}

export interface AppServiceAWSAuth {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
}

export interface AppServiceAWS {
  Auth?: AppServiceAWSAuth;
}

export interface AppServiceApi {
  baseUrl: string;
  loginRedirectEndpoint: string;
  logoutEndpoint: string;
  getTokenEndpoint: string;
  refreshTokenEndpoint: string;
  getConfigEndpoint: string;
  getStoresEndpoint: string;
  signatureEndpoint: string;
  customerEndpoint: string;
  customerStatusEndpoint: string;
  encodeSignature: boolean;
}
export interface AppServiceOidc {
  baseUrl: string;
  loginRedirectEndpoint: string;
  scope: string;
  userPoolWebClientId: string;
  getTokenEndpoint: string;
  getTokenHeaders: any;
  logoutRedirectEndpoint: string;
  clientId: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  codeVerifier: string;
  userInfoEndpoint: string;
  loginRedirectParams: any;
}

export type AppService =
  | AppServiceAWSAuth
  | AppServiceGoogleAnalytics
  | AppServiceGoogleMaps
  | AppServiceApi
  | AppServiceOidc
  | any;

export interface AppServicesConfig {
  [serviceKey: string]: AppService;
}

export interface AppZone {
  /**
   * The main language(s) (BCP 47 format) for this zone.
   */
  languages: string | string[];
  /**
   * All languages (BCP 47 format) which can be used within this zone.
   */
  supportedLanguages: string | string[];
  /**
   * Contact email address for the brand's zone.
   */
  brandContactEmail?: string;
  /**
   * Contact postal address for the brand's zone.
   */
  brandContactAddress?: string;
  // a whole potential copy of config structure to be defined by zone
  config: AppConfig;
}

/**
 * Zones define specific geographic areas and languages available that apply to
 * legal terms & conditions.
 */
export type AppZoneConfig =
  | {
      [zoneKey: string]: AppZone;
    }
  | {};

export interface AppNotificationConfig {
  /**
   * The LocalStorage key name.
   */
  localStorageKey: string;
  /**
   * How long notifications hang around for.
   *
   * Default value is for 1 week (7 days).
   */
  timeExpires: number;
  /**
   * How many notifications to show per page.
   */
  showPerPage: number;
}

export interface AppLanguagesConfig {
  /**
   * Supported languages in BCP 47 format, e.g. `["en", "zh-Hant", "pt-BR"]`
   *
   * It's important to state all supported languages, since this will be used
   * to programmatically load support files for the languages (country names,
   * time label formatters, non-roman character translators, etc...)
   */
  supportedLanguages: string[];
  /**
   * Non-roman languages in BCP 47 format, e.g. "ja", "zh", "ar"
   *
   * Most computer systems prefer roman/latin-based characters, so when
   * foreign customers are entering in their information into a latin-character
   * based computer system (or for latin-character based language users of those
   * computer systems) we might need to display extra information to the
   * customer or process their data differently.
   */
  nonRomanLanguages: string[];
  /**
   * The path to asynchronously load translation files.
   *
   * This should be compatible with the `i18next-xhr-backend` `loadPath` option.
   */
  loadPath: string;
}

export interface AppCustomiseConfig {
  header: {
    /**
     * Show the setup button in the header.
     *
     * @default true
     */
    showSetup: boolean;
    /**
     * Show the language change in the footer.
     *
     * @default true
     */
    showLanguage: boolean;
    /**
     * Show the notifications button in the header.
     *
     * @default true
     */
    showNotifications: boolean;
  };
  footer: {
    /**
     * Show the language changer in the footer.
     *
     * @default false
     */
    showLanguage: boolean;
  };
  languageSwitcher: {
    /**
     * If a zone has a `languages` property, only the languages specified there
     * will be used in the language chooser.
     *
     * @default false
     */
    showOnlyZoneLanguages: boolean;
  };
  notifications: {
    /**
     * Enable displaying customer data on successful notifications.
     *
     * @default false
     */
    enableDisplayCustomer: boolean;
  };
  verifySalesPerson: {
    /**
     * Enable the verify sales person feature.
     *
     * @default true
     */
    enable: boolean;
    /**
     * Capitalise the SA ID when logging in, e.g. "abc001" -> "ABC001"
     *
     * @default true
     */
    capitaliseId: boolean;
  };
  actions: {
    remoteCustomerButtonTheme: string;
  };
  searchCustomer: {
    /**
     * Show the search customer feature.
     *
     * @default false
     */
    enable: boolean;
  };
  customer: {
    /**
     * Show the additional info fields for the SA to fill in when the customer
     * has completed the form.
     *
     * @default true
     */
    defaultLang: string;
    enableAdditionalInfo: boolean;

    enableAdvisorForm: boolean;
    enableSummary: boolean;
    enableResultsModal: boolean;
    enableRemoteCustomer: boolean;
    summaryFields: any[];
    /**
     * Check to see if the additional info should be shown.
     *
     * Works just like `ConfigurableFormElementConfig.showOnlyIf`.
     */
    checkShowAdditionalInfo:
      | boolean
      | ValidationRules
      | ConfigurableFormShowOnlyIfFn;
  };
  authentication: {
    /**
     * Show or hide the "Forgot/reset password" button on the authentication form.
     *
     * @default true
     */
    enableResetPassword: boolean;
    redirect: boolean;
  };
  fields: {
    /**
     * Show or hide "required" extra label for required fields
     *
     * @default true
     */
    showRequiredLabel: boolean;
    markRequiredWithSymbol: string | null | false | 0;
    /**
     * Show or hide "optional" extra label on non-required fields
     *
     * @default false
     */
    showOptionalLabel: boolean;

    picklistValues?: PicklistFieldConfig[];
  };
}

export interface PicklistFieldConfig {
  name: string;
  values?: string[];
  options?: [
    {
      label: string;
      value: string;
    }
  ];
}

export interface AppConfig {
  /**
   * App ID. Also used for brand-specific folder names.
   *
   * Most likely this will be a computer-readable version of the brand,
   * e.g. "lvmh"
   */
  id: MaisonIdentifier;
  /**
   * The human-readable name of the brand, e.g. "LVMH Digital"
   */
  brand: string;
  /**
   * A contact email address for customers to contact the brand.
   */
  brandContactEmail: string;
  /**
   * A contact post address for customers to contact the brand.
   */
  brandContactAddress: string;
  /**
   * The human-readable name of the app, e.g. "In-store Customer Card App"
   */
  name: string;
  /**
   * The path to the API. Can be absolute or relative URL (relative from `PUBLIC_URL`).
   */
  api: string;
  /**
   * Languages configuration.
   */
  languages: AppLanguagesConfig;
  /**
   * Names of legal zones for Terms & Conditions and any other meta data.
   *
   * The store's configured legal zone (previously known as "region") can also
   * be used to show/hide form fields (configured in the `fields` object).
   */
  zones: AppZoneConfig;
  /**
   * Either the store data itself, or a location of where to download store data.
   *
   * By default it will retrieve the data from the API: `${config.api}/store`
   */
  stores: undefined | null | string | LocalBusiness[];
  /**
   * Either the place data itself, or a location of where to download place data.
   *
   * By default it will retrieve the data from the API: `${config.api}/place`
   */
  places: undefined | null | string | Place[];
  /**
   * Either the country data itself, or a location of where to download country data.
   *
   * By default it will retrieve the data from the API: `${config.api}/country`
   */
  countries: undefined | null | string | Place[];
  /**
   * Either the list of Honorific Prefixes (AKA customer "Title"), or a location of where to download Honorific Prefix data.
   *
   * By default it will retrieve the data from the API: `${config.api}/customer/honorificPrefix` (?)
   */
  honorificPrefixes: undefined | null | string | ReactSelectOptionObject[];
  /**
   * Notification configuration.
   */
  notifications: AppNotificationConfig;
  /**
   * Error codes to output to assist with IT help desk.
   */
  errorCodes: {
    APP: number | string;
    NETWORK: number | string;
    SERVER: number | string;
    DATA_CONNECTION: number | string;
    DATA_ERROR: number | string;
  };
  /**
   * Top-level app customisation.
   */
  customise: AppCustomiseConfig;
  /**
   * Form
   */
  forms: {
    // Form specific schema (@TODO: see if it is really necessary)
    schema: string;
    // one filepath per form step (Customer Data, Consent, Additional Info)
    fields: string[];
    // Form configs the customer data form
    customer: ConfigurableFormConfig[];
  };
  /**
   * Data transforms
   */
  transforms: {
    customer: {
      read: TransformMap;
      write: TransformMap;
      response: TransformMap;
    };
  };
  /**
   * Configuration for third-party services (Google APIs, etc.).
   */
  services: AppServicesConfig;
}
